import React, {useEffect, useState, useCallback} from 'react';
import {observer} from 'mobx-react-lite';
import cx from 'classnames';
import {Flex, Icon, PriorityIcon, Separator} from '@priority-software/priority-style-react';
import {isRTL} from '../../Localization';
import styles from './styles.module.scss';
import MainPage from './Pages/Main.page';
import {Pages} from './consts';
import ReportGeneratorPage from './Pages/Report.page';
import {CustomQueryClientProvider} from '../../hooks/useQueryClientProvider';
import Header from './componenets/Header';
import {useReportQuery} from './useReportsQuery';
import {reportGeneratorsStore} from './stores/ReportGeneratorsStore';
import {featuresIDs, shouldShowFeature} from '../FeaturePopover/constants/map';
import {updateBlackList} from '../FeaturePopover/actions/feature';
import {analyticsInitAutomationExport} from './AutomationExports';
import ChartGeneratorPage from './Pages/Chart.page';
import ChartDisplayPage from './Pages/ChartDisplay.page';
import AnalysisPanel from './componenets/AnalysisPanel/AnalysisPanel';
import {ChartContextProvider} from './contexts/ChartContext';

const getToggleButtonIcon = (showAnalysisPanel: boolean, rtl: boolean) => {
    if (showAnalysisPanel) {
        return rtl ? PriorityIcon.arrowLeft : PriorityIcon.arrowRight;
    } else {
        return rtl ? PriorityIcon.arrowRight : PriorityIcon.arrowLeft;
    }
};

const AnalyticsComp = observer(() => {
    const {currentScreen, close} = reportGeneratorsStore;
    const {isLoading} = useReportQuery(currentScreen);
    const [showAnalysisPanel, setShowAnalysisPanel] = useState(true);
    const [hideAnalysisPanelContainer, setHideAnalysisPanelContainer] = useState(false);

    // Toggle analysis panel visibility
    const toggleAnalysisPanel = useCallback(() => {
        setShowAnalysisPanel(prev => !prev);
    }, []);

    const toggleHideAnalysisPanelContainer = useCallback(
        (hide: boolean) => {
            setHideAnalysisPanelContainer(hide);
        },
        [hideAnalysisPanelContainer]
    );

    // Update context when showAnalysisPanel changes
    useEffect(() => {
        // This will be handled by passing the prop to ChartContextProvider
    }, [showAnalysisPanel]);

    // Get the text direction (RTL or LTR)
    const rtl = isRTL();

    // Initialize analytics features
    useEffect(() => {
        if (shouldShowFeature(featuresIDs.analytics) || shouldShowFeature(featuresIDs.analytics2)) {
            updateBlackList(featuresIDs.analytics);
            updateBlackList(featuresIDs.analytics2);
        }
        analyticsInitAutomationExport();
    }, []);

    const renderToggleButton = useCallback(() => {
        return (
            <div className={styles.toggleButton}>
                <Icon
                    onClick={toggleAnalysisPanel}
                    componentClasses={cx(styles.toggleButtonIcon, {
                        [styles.expandedPanel]: showAnalysisPanel,
                        [styles.minimiziedPanel]: !showAnalysisPanel
                    })}
                    icon={getToggleButtonIcon(showAnalysisPanel, rtl)}
                    aria-label={
                        showAnalysisPanel ? 'Collapse analysis panel' : 'Expand analysis panel'
                    }
                />
            </div>
        );
    }, [showAnalysisPanel, toggleAnalysisPanel, rtl]);

    // Render chart display page with analysis panel
    const renderChartDisplayWithAnalysis = useCallback(() => {
        return (
            <ChartContextProvider showAnalysisPanel={showAnalysisPanel}>
                <Flex direction="row" componentClasses={styles.fullHeightContainer}>
                    {/* Main content area */}
                    <Flex
                        direction="column"
                        componentClasses={cx(styles.mainContentContainer, {
                            [styles.extraMargin]: showAnalysisPanel
                        })}
                    >
                        <Flex justifyContent="space-between" alignItems="center">
                            <Header onClose={() => close(currentScreen.session)} />
                        </Flex>
                        <Flex componentClasses={styles.chartContainer}>
                            <ChartDisplayPage />
                        </Flex>
                    </Flex>
                    {!showAnalysisPanel && (
                        <div className={styles.closedAnalysisWrapper}>
                            <Flex
                                componentClasses={styles.closedAnalysisContainer}
                                justifyContent="center"
                                alignItems="center"
                                direction="column"
                            >
                                {renderToggleButton()}
                                <Separator componentClasses={styles.separator} align="horizontal" />
                            </Flex>
                        </div>
                    )}

                    <div
                        className={cx(styles.analysisPanelWrapper, {
                            [styles.analysisPanelHidden]: !showAnalysisPanel,
                            [styles.hideContainer]: hideAnalysisPanelContainer
                        })}
                    >
                        <Flex componentClasses={styles.analysisPanelContainer}>
                            {renderToggleButton()}
                            <AnalysisPanel onHideContainer={toggleHideAnalysisPanelContainer} />
                        </Flex>
                    </div>
                </Flex>
            </ChartContextProvider>
        );
    }, [showAnalysisPanel, hideAnalysisPanelContainer, currentScreen, close]);

    // Render standard page layout
    const renderStandardPage = useCallback(() => {
        return (
            <>
                <Header onClose={() => close(currentScreen.session)} />
                {currentScreen.currentPage === Pages.main && <MainPage isLoading={isLoading} />}
                {currentScreen.currentPage === Pages.report && <ReportGeneratorPage />}
                {currentScreen.currentPage === Pages.chart && <ChartGeneratorPage />}
            </>
        );
    }, [currentScreen, close, isLoading]);

    return (
        <div className={styles.container}>
            {currentScreen.currentPage === Pages.chartDisplay
                ? renderChartDisplayWithAnalysis()
                : renderStandardPage()}
        </div>
    );
});

export const Analytics = () => (
    <CustomQueryClientProvider>
        <AnalyticsComp />
    </CustomQueryClientProvider>
);
